import { useRef } from "react"

import { useCountUp } from "../hooks/use-count-up"

type CountUpProps = {
  /** Pre-formatted display string (e.g. "3,120", "6.0%", "260 MEUR", "5.4 / 10"). */
  value: string
  /** Total animation duration in ms. Defaults to the hook's 800ms. */
  duration?: number
}

/**
 * Renders a numeric display string with its numbers "counting up" from 0 on the
 * FIRST mount only. Subsequent `value` changes render instantly with no tween —
 * the KPIs are polled (~1.4s) and a mid-animation interpolated number would read
 * as a misleading live value. Preserves separators, decimals and any non-numeric
 * chrome, and respects prefers-reduced-motion.
 *
 * Drop-in replacement for a plain `{value}` inside a leaf display element.
 */
export function CountUp({ value, duration }: CountUpProps) {
  const initialValueRef = useRef(value)
  const hasChangedRef = useRef(false)
  // Computed during render (not in an effect, which would fire after the first
  // paint and cancel the mount animation). Once the value ever changes, every
  // future render passes duration 0 → the hook's instant-render path.
  if (value !== initialValueRef.current) hasChangedRef.current = true

  const display = useCountUp({ value, duration: hasChangedRef.current ? 0 : duration })
  return <>{display}</>
}
